import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Shared nerdle labs infrastructure (think-aloud methodology, reusable across games).
import { AdLayout } from '../../shared/components/AdLayout'
import { BrowserGate } from '../../shared/components/BrowserGate'
import { Brand } from '../../shared/components/Brand'
import { ReflectionNotice } from '../../shared/components/ReflectionNotice'
import { StatusScreen } from '../../shared/components/StatusScreen'
import { OtherGamesLinks } from '../../shared/components/OtherGamesLinks'
import { labsHomeUrl } from '../../shared/labs/labsNav'
import { hasConsented, markConsented } from '../../shared/labs/progress'
import { browserGateReason, captureEnv, enrichCountry, CaptureEnv } from '../../shared/lib/env'
import { uuidv4 } from '../../shared/lib/uuid'
import { useSpeechRecognition, TranscriptEntry } from '../../shared/labs/useSpeechRecognition'
import { useStreamingTranscription } from '../../shared/labs/useStreamingTranscription'
import { getSttEngine } from '../../shared/labs/sttEngine'
import { getRetroAfter } from '../../shared/labs/retroConfig'
import { getSourceTag } from '../../shared/labs/sourceTag'
import { hasSeenPuzzle, markPuzzleSeen, seenPuzzleKeys } from '../../shared/labs/seenPuzzles'
import { useThinkingEnergy, ENERGY_CONFIG } from '../../shared/labs/useThinkingEnergy'
import { InteractionEvent, PuzzleTrace, PuzzleRetro, CaptureMode, TRACE_SCHEMA_VERSION, RETRO_SCHEMA_VERSION } from '../../shared/labs/types'
import { saveConsent, savePuzzleTrace, savePuzzleRetro, savePanelSignup } from '../../shared/labs/sessionStore'
import { readNerdleIdentity } from '../../shared/labs/panelAuth'
import { ConfirmedSegment, formatWithPauses } from '../../shared/labs/transcript'

// 24-game-specific UI + logic.
import { ConsentScreen } from './components/ConsentScreen'
import { PuzzleView } from './components/PuzzleView'
import { PuzzleStage } from './components/PuzzleStage'
import { AnswerKeypad } from './components/AnswerKeypad'
import { SummaryScreen } from './components/SummaryScreen'
import { PanelSignup } from './components/PanelSignup'
import { RetroScreen, RetroAnswer } from './components/RetroScreen'
import { SurveyScreen } from '../../shared/labs/SurveyScreen'
import { labsFeedbackSurvey, surveyCompletedAt, markSurveyDone } from '../../shared/labs/survey'
import { track, setLabsUser } from '../../shared/labs/analytics'
import { addStreamedMs, overDailyCap } from '../../shared/labs/streamLimit'
import {
  practiceSession,
  buildTestSession,
  buildSubgoalSession,
  buildDivisionSession,
  pickPlayPuzzle,
  buildExtensionPuzzles,
  assertPoolSolvable,
  SessionPuzzle,
  Difficulty,
} from './game24/puzzles'
import { exampleSolution } from './game24/solver'
import { judgeGame24 } from './game24/evaluate'
import { prettyExpr } from './game24/format'
import { scoreTranscript, MatchResult } from './game24/spokenMatch'

const REFLECTION_SECONDS = 5
const STT_ENGINE = getSttEngine()
const RETRO = getRetroAfter() // retrospective ("aha") follow-up regime (see retroConfig)
const SOURCE = getSourceTag() // acquisition attribution: ?src=email&t=<token> (see sourceTag)
const EXTENSION_PUZZLES = 5 // opt-in "keep going" research puzzles after the core 10

// Phase-2 arm allocation. When enabled, a test session is randomly assigned to one of three arms
// at entry. Full-set is the invariant control (Wurgaft replication + drift monitor); subgoal and
// division are the contrast arms. Weights are relative (need not sum to 1); full-set is held at its
// share and only subgoal/division are rebalanced if one lags. Off until go-live.
type Arm = 'full-set' | 'subgoal' | 'division'
const PHASE2_ENABLED = true
const ARM_WEIGHTS: Record<Arm, number> = { 'full-set': 1, subgoal: 1, division: 1 }
const pickArm = (): Arm => {
  const arms = Object.entries(ARM_WEIGHTS) as [Arm, number][]
  const total = arms.reduce((s, [, w]) => s + w, 0)
  let r = Math.random() * total
  for (const [a, w] of arms) if ((r -= w) < 0) return a
  return 'full-set'
}
const buildArmSession = (a: Arm): { conditionId: number; puzzles: SessionPuzzle[] } =>
  a === 'subgoal' ? buildSubgoalSession() : a === 'division' ? buildDivisionSession() : buildTestSession()

// Dev-only QA override: ?arm=subgoal|division|full-set forces the arm so each can be walked through
// deterministically in development. Never active in a production build.
const ARM_OVERRIDE: Arm | null = (() => {
  if (process.env.NODE_ENV === 'production') return null
  try {
    const a = new URLSearchParams(window.location.search).get('arm')
    return a === 'subgoal' || a === 'division' || a === 'full-set' ? a : null
  } catch {
    return null
  }
})()
const PUZZLE_LIMIT_MS = 3 * 60 * 1000 // Wurgaft's 3-minute per-puzzle limit → auto-end unsolved
const NUDGE_SILENCE_MS = 3000
const COMMIT_WORD = /\bcommit\b/i

// localStorage: has the player completed the 10-game test (which unlocks free play)?
const TEST_DONE_KEY = 'labs24_test_complete'
const isTestDone = (): boolean => {
  try {
    return localStorage.getItem(TEST_DONE_KEY) === '1'
  } catch {
    return false
  }
}
const markTestDone = () => {
  try {
    localStorage.setItem(TEST_DONE_KEY, '1')
  } catch {
    /* noop */
  }
}

const verdictNote = (v: ReturnType<typeof judgeGame24> | null, numbers: number[]): string | null => {
  if (!v || v.correct) return null
  switch (v.reason) {
    case 'wrong-numbers':
      return `You need to use all four numbers — ${numbers.join(', ')} — each exactly once.`
    case 'wrong-value':
      return v.value !== null ? `That comes to ${v.value}, not 24.` : 'That doesn’t come to 24.'
    case 'parse-error':
      return 'Couldn’t read that — check the brackets and operators.'
    case 'empty':
      return 'Looks like nothing was entered.'
    default:
      return null
  }
}

type AnswerMode = 'voice-detected' | 'typed' | 'pass' | 'timeout'

type Phase =
  | 'consent'
  | 'mic-error'
  | 'daily-limit' // 60-min/day streamed-audio cap reached (see streamLimit)
  | 'mode-choice' // returning (unlocked) player: free play or retake the test
  | 'practice-intro'
  | 'test-intro'
  | 'play-intro' // difficulty selector for free play
  | 'playing'
  | 'reflection'
  | 'detected'
  | 'entry'
  | 'verdict'
  | 'retro' // retrospective ("aha") follow-up after a solved puzzle
  | 'continue-offer' // opt-in to a few more research puzzles after the core test
  | 'summary'
  | 'survey' // end-of-play feedback survey
  | 'panel' // re-engagement sign-up (logged-out players, after feedback)

function Game24() {
  const gateReason = useMemo(() => browserGateReason(), [])

  const [phase, setPhase] = useState<Phase>('consent')
  // Re-engagement panel: capture logged-in players on their FIRST test puzzle (see
  // finishAdvance — engagement, not completion, so partial players are included too);
  // route logged-out players through the sign-up card once, after the feedback step.
  const panelCapturedRef = useRef(false)
  const panelDoneRef = useRef(false)
  const isLoggedIn = () => {
    try {
      return !!localStorage.getItem('lbl_token')
    } catch {
      return false
    }
  }
  const afterFeedback = () => setPhase(!isLoggedIn() && !panelDoneRef.current ? 'panel' : 'play-intro')
  const [confirmedSegments, setConfirmedSegments] = useState<ConfirmedSegment[]>([])
  const [interimText, setInterimText] = useState('')
  const [tsBase, setTsBase] = useState(0)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [autoMatch, setAutoMatch] = useState<MatchResult | null>(null)
  const [typedResult, setTypedResult] = useState<ReturnType<typeof judgeGame24> | null>(null)
  const [retroSpoken, setRetroSpoken] = useState('') // live spoken retro answer (finals)
  const [retroInterim, setRetroInterim] = useState('')
  const [showPassConfirm, setShowPassConfirm] = useState(false) // "keep going vs give up" modal
  const [retroCommitSignal, setRetroCommitSignal] = useState(0) // bumped when "commit" heard during retro

  // ---- mode/session state ----
  const [mode, setMode] = useState<CaptureMode>('practice')
  const [conditionId, setConditionId] = useState<number | null>(null)
  const [arm, setArm] = useState<Arm | null>(null) // Phase-2 arm for the current test session
  const [playDifficulty, setPlayDifficulty] = useState<Difficulty | undefined>(undefined)
  const [sessionPuzzles, setSessionPuzzles] = useState<SessionPuzzle[]>([])
  const [puzzleIndex, setPuzzleIndex] = useState(0)
  const puzzle = sessionPuzzles[puzzleIndex] as SessionPuzzle | undefined
  const solutionExample = useMemo(() => (puzzle ? exampleSolution(puzzle.numbers) : null), [puzzle])
  const isLastInSession = puzzleIndex >= sessionPuzzles.length - 1
  const progressLabel =
    mode === 'test'
      ? `Puzzle ${puzzleIndex + 1} of ${sessionPuzzles.length}`
      : mode === 'practice'
      ? `Warm-up ${puzzleIndex + 1} of ${sessionPuzzles.length}`
      : `Free play · puzzle ${puzzleIndex + 1}`

  // ---- refs (per-puzzle capture buffers) ----
  const sessionRef = useRef<{ id: string; startedAtISO: string } | null>(null)
  const envRef = useRef<CaptureEnv | null>(null)
  const transcriptRef = useRef<TranscriptEntry[]>([])
  const eventsRef = useRef<InteractionEvent[]>([])
  const puzzleStartRef = useRef<{ iso: string; perf: number } | null>(null)
  const committedTranscriptRef = useRef<string>('')
  const hasCommittedRef = useRef(false)
  const committedAtRef = useRef<number>(0)
  const reflectionEndRef = useRef<number>(0)
  const dimRef = useRef(false)
  const sampleCounterRef = useRef(0)
  const lastActivityRef = useRef(0)
  const autoRejectedRef = useRef(false)
  const lastTypedRef = useRef<string | null>(null)
  const tsBaseRef = useRef(0)
  const sessionTracesRef = useRef<PuzzleTrace[]>([]) // completed traces in the current mode session
  const retroTranscriptRef = useRef<TranscriptEntry[]>([]) // spoken retro answer, captured separately
  const retroPromptedAtRef = useRef<number>(0)
  const retroPromptedISORef = useRef<string>('')
  const coreLenRef = useRef(0) // length of the core test set (before any extension)
  const extendedRef = useRef(false) // player opted into the extra research puzzles
  const endUnsolvedRef = useRef<(reason: 'pass' | 'timeout') => void>(() => {}) // latest, for the timer

  const pushEvent = useCallback((e: InteractionEvent) => {
    eventsRef.current.push(e)
  }, [])

  useEffect(() => {
    assertPoolSolvable()
    track('labs_consent_view')
  }, [])

  // Wurgaft's 3-minute per-puzzle limit: while solving, arm a timer for the time remaining
  // in this puzzle; if it fires the trial ends unsolved (reason 'timeout'). Committing
  // leaves 'playing', which clears it via cleanup, so it can't fire after an answer.
  useEffect(() => {
    if (phase !== 'playing') return
    const start = puzzleStartRef.current?.perf ?? performance.now()
    const fireIfOver = () => {
      if (performance.now() - start >= PUZZLE_LIMIT_MS) endUnsolvedRef.current('timeout')
    }
    const remaining = Math.max(0, PUZZLE_LIMIT_MS - (performance.now() - start))
    const id = window.setTimeout(fireIfOver, remaining)
    // A backgrounded/frozen tab (esp. mobile) suspends the timer while performance.now()
    // keeps counting, so an abandoned puzzle would otherwise record hours. Re-check on wake
    // so an overrun the sleeping timer missed ends immediately instead of running on.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fireIfOver()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [phase, puzzleIndex])

  // ---- thinking-energy mechanic ----
  const energy = useThinkingEnergy({
    running: phase === 'playing' || phase === 'reflection',
    tsBase,
    resetKey: `${mode}-${puzzleIndex}`,
    onSample: (s) => {
      const nowDim = s.energy <= ENERGY_CONFIG.dimThreshold
      if (nowDim !== dimRef.current) {
        dimRef.current = nowDim
        pushEvent({ ts: s.ts, type: nowDim ? 'dim_on' : 'dim_off' })
      }
      sampleCounterRef.current += 1
      if (sampleCounterRef.current % Math.max(1, Math.round(1000 / ENERGY_CONFIG.tickMs)) === 0) {
        pushEvent({ ts: s.ts, type: 'energy_sample', energy: s.energy, speaking: s.speaking })
      }
    },
  })

  // Commit the answer (button OR spoken "commit"). Guarded to fire once per puzzle.
  const handleCommit = useCallback(() => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true
    const segments: ConfirmedSegment[] = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => ({ text: t.text.trim(), ts: t.ts }))
      .filter((s) => s.text)
    committedTranscriptRef.current = formatWithPauses(segments)
    committedAtRef.current = performance.now()
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'commit', value: committedTranscriptRef.current })
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'reflection_start' })
    setPhase('reflection')
  }, [tsBase, pushEvent])

  // ---- transcription (both engines share these handlers; only one is active) ----
  const onTranscript = (entry: TranscriptEntry) => {
    if (phase === 'retro') {
      // Route retro speech into its own buffer so it never contaminates the puzzle trace.
      if (entry.isFinal) {
        retroTranscriptRef.current.push(entry)
        const t = entry.text.trim()
        if (t) setRetroSpoken((prev) => (prev ? `${prev} ${t}` : t))
        setRetroInterim('')
        // Same commit gesture as solving: saying "commit" submits the retro answer.
        if (COMMIT_WORD.test(entry.text)) setRetroCommitSignal((n) => n + 1)
      } else {
        setRetroInterim(entry.text)
      }
      return
    }
    transcriptRef.current.push(entry)
    energy.registerActivity()
    lastActivityRef.current = performance.now()
    if (entry.isFinal) {
      const text = entry.text.trim()
      if (text) setConfirmedSegments((prev) => [...prev, { text, ts: entry.ts }])
      setInterimText('')
    } else {
      setInterimText(entry.text)
    }
    if (phase === 'playing' && COMMIT_WORD.test(entry.text)) handleCommit()
  }
  const onSpeechStart = () => {
    energy.registerActivity()
    lastActivityRef.current = performance.now()
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'speech_start' })
  }
  const onSpeechEnd = () => pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'speech_end' })
  const onPermissionDenied = () => setPhase('mic-error')

  const speechActive = phase === 'playing' || phase === 'reflection' || phase === 'retro'
  useSpeechRecognition({
    active: speechActive && STT_ENGINE === 'webspeech',
    tsBase,
    onTranscript,
    onSpeechStart,
    onSpeechEnd,
    onPermissionDenied,
  })
  const streaming = useStreamingTranscription({
    active: speechActive && STT_ENGINE !== 'webspeech',
    tsBase,
    provider: STT_ENGINE === 'google' ? 'google' : 'deepgram',
    onTranscript,
    onSpeechStart,
    onSpeechEnd,
    onPermissionDenied,
  })

  // ---- per-puzzle lifecycle ----
  const startPuzzle = useCallback(
    (index: number) => {
      if (overDailyCap()) {
        setPhase('daily-limit')
        return
      }
      transcriptRef.current = []
      eventsRef.current = []
      committedTranscriptRef.current = ''
      hasCommittedRef.current = false
      autoRejectedRef.current = false
      lastTypedRef.current = null
      dimRef.current = false
      sampleCounterRef.current = 0
      setConfirmedSegments([])
      setInterimText('')
      setAutoMatch(null)
      setTypedResult(null)
      setShowPassConfirm(false)
      puzzleStartRef.current = { iso: new Date().toISOString(), perf: performance.now() }
      lastActivityRef.current = performance.now()
      pushEvent({ ts: Math.round(performance.now() - tsBaseRef.current), type: 'mic_on' })
      setPuzzleIndex(index)
      setPhase('playing')
    },
    [pushEvent]
  )

  // ---- mode entry ----
  const beginPractice = useCallback(() => {
    track('labs_practice_start')
    setMode('practice')
    setConditionId(null)
    setArm(null)
    sessionTracesRef.current = []
    setSessionPuzzles(practiceSession())
    startPuzzle(0)
  }, [startPuzzle])

  const beginTest = useCallback(() => {
    const chosen: Arm = ARM_OVERRIDE ?? (PHASE2_ENABLED ? pickArm() : 'full-set')
    const { conditionId: cid, puzzles } = buildArmSession(chosen)
    track('labs_test_start', { condition_id: cid, arm: chosen })
    setMode('test')
    setArm(chosen)
    setConditionId(cid)
    sessionTracesRef.current = []
    coreLenRef.current = puzzles.length
    extendedRef.current = false
    setSessionPuzzles(puzzles)
    startPuzzle(0)
  }, [startPuzzle])

  const beginPlay = useCallback(
    (difficulty?: Difficulty) => {
      track('labs_play_start', { difficulty: difficulty || 'any' })
      setMode('play')
      setConditionId(null)
      setArm(null)
      setPlayDifficulty(difficulty)
      sessionTracesRef.current = []
      setSessionPuzzles([pickPlayPuzzle(difficulty)])
      startPuzzle(0)
    },
    [startPuzzle]
  )

  // ---- start / consent / mic ----
  const afterMic = useCallback(() => {
    if (overDailyCap()) setPhase('daily-limit')
    else if (isTestDone()) setPhase('mode-choice')
    else setPhase('practice-intro')
  }, [])

  const requestMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach((t) => t.stop())
      track('labs_mic_result', { granted: 'yes' })
      afterMic()
    } catch {
      track('labs_mic_result', { granted: 'no' })
      setPhase('mic-error')
    }
  }, [afterMic])

  const handleStart = useCallback(async () => {
    markConsented() // latch the one-time labs consent (skips the screen on future visits/games)
    const id = uuidv4()
    const startedAtISO = new Date().toISOString()
    const env = captureEnv()
    sessionRef.current = { id, startedAtISO }
    envRef.current = env
    setLabsUser({ labs_session_id: id, labs_stt_engine: STT_ENGINE })
    track('labs_consent', { consented: 'yes' })
    const base = performance.now()
    tsBaseRef.current = base
    setTsBase(base)
    saveConsent({
      sessionId: id,
      consented: true,
      env,
      retroAfterN: Number.isFinite(RETRO.n) ? RETRO.n : null,
      retroCode: RETRO.code,
      sttEngine: STT_ENGINE,
      source: SOURCE,
    }).catch(() => {})
    enrichCountry().then((c) => {
      if (c && envRef.current) envRef.current.country = c
    })
    await requestMic()
  }, [requestMic])

  // Returning player who already consented (this device) → skip the consent screen and start.
  useEffect(() => {
    if (hasConsented()) handleStart()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- reflection → auto-score ----
  const endReflection = useCallback(() => {
    if (!puzzle) return
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'reflection_end' })
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'mic_off' })
    reflectionEndRef.current = performance.now()
    const rawSpoken = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => t.text)
      .join(' ')
    const match = scoreTranscript(rawSpoken, puzzle.numbers)
    setAutoMatch(match)
    if (match.fired) {
      pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_detected' })
      setPhase('detected')
    } else {
      setPhase('entry')
    }
  }, [puzzle, tsBase, pushEvent])

  // ---- build + save one trace, then advance by mode ----
  const buildTrace = (answerMode: AnswerMode, typed: ReturnType<typeof judgeGame24> | null, typedRaw: string | null): PuzzleTrace => {
    const p = puzzle!
    const pStart = puzzleStartRef.current!
    const match = autoMatch
    return {
      schemaVersion: TRACE_SCHEMA_VERSION,
      sessionId: sessionRef.current!.id,
      mode,
      conditionId: mode === 'test' ? conditionId : null,
      arm: mode === 'test' ? arm : null,
      band: p.band ?? null,
      trialIndex: puzzleIndex,
      sitting: 1,
      resumed: false,
      aborted: false,
      puzzleId: p.id,
      numbers: p.numbers,
      difficulty: p.difficulty,
      decile: p.decile ?? null,
      humanRank: p.rank,
      humanSolveRate: p.solveRate,
      transcript: transcriptRef.current,
      events: eventsRef.current,
      committedTranscript: committedTranscriptRef.current,
      sttEngine: STT_ENGINE,
      exampleSolution: solutionExample,
      autoFired: match?.fired ?? false,
      autoMethod: match?.method ?? null,
      autoStars: match?.stars ?? 0,
      autoSimilarity: match?.similarity ?? 0,
      autoMatchedSolution: match?.matchedSolution ?? null,
      autoRejected: autoRejectedRef.current,
      answerMode,
      typedAnswer: typedRaw,
      typedValue: typed ? typed.value : null,
      typedReason: typed ? typed.reason : null,
      correct: answerMode === 'voice-detected' ? true : !!typed?.correct,
      correctnessReason: answerMode,
      puzzleStartedAt: pStart.iso,
      // Clamp at the 3-min limit: a puzzle can never legitimately run longer, so this stops a
      // backgrounded-tab overrun (suspended timer) from recording hours and skewing durations/spend.
      puzzleDurationMs: Math.min(Math.round(committedAtRef.current - pStart.perf), PUZZLE_LIMIT_MS),
      reflectionDurationMs: Math.round(reflectionEndRef.current - committedAtRef.current),
      env: envRef.current!,
      consented: true,
      source: SOURCE,
      // First exposure iff this device hasn't seen this puzzle before now (advance() marks it
      // seen just after). Read here so the flag reflects state *before* this attempt.
      firstExposure: !hasSeenPuzzle(p.numbers),
    }
  }

  // The mode-specific "what next" branch — run straight after a puzzle, or after its
  // retrospective follow-up, so the retro sits between a solved puzzle and the next.
  const finishAdvance = () => {
    if (mode === 'practice') {
      // Advance through the two distinct warm-up puzzles regardless of outcome (our
      // voice "solved" detection is fuzzier than Wurgaft's typed input, so looping on
      // the same puzzle just looked like a duplicate).
      if (puzzleIndex < sessionPuzzles.length - 1) startPuzzle(puzzleIndex + 1)
      else setPhase('test-intro')
    } else if (mode === 'test') {
      // Capture the logged-in identity on the FIRST test puzzle (engagement, not
      // completion), so partial players are captured too. Deduped once per session.
      if (isLoggedIn() && sessionRef.current && !panelCapturedRef.current) {
        panelCapturedRef.current = true
        const idy = readNerdleIdentity()
        savePanelSignup({
          sessionId: sessionRef.current.id,
          method: 'logged-in',
          lblToken: idy?.lblToken ?? null,
          email: idy?.email ?? null,
        }).catch(() => {})
      }
      if (puzzleIndex < sessionPuzzles.length - 1) {
        startPuzzle(puzzleIndex + 1)
      } else {
        // End of the currently-loaded test set. Offer the opt-in extension once, if the
        // retro regime is active; otherwise wrap up.
        markTestDone()
        if (!extendedRef.current) {
          track('labs_test_complete', { solved_count: sessionTracesRef.current.filter((t) => t.correct).length })
        }
        // Extension ("keep going") is full-set only: the contrast arms are a fixed 5 + 5 draw, so
        // they wrap up after the core 10. (The per-puzzle retro follow-up still runs on all arms.)
        const retroActive = Number.isFinite(RETRO.n) && RETRO.n <= coreLenRef.current - 1
        if (arm === 'full-set' && retroActive && !extendedRef.current) {
          track('labs_continue_offer')
          setPhase('continue-offer')
        } else setPhase('summary')
      }
    } else {
      // play: load another puzzle and keep going
      setSessionPuzzles((prev) => [...prev, pickPlayPuzzle(playDifficulty)])
      startPuzzle(puzzleIndex + 1)
    }
  }

  const advance = (answerMode: AnswerMode, typed: ReturnType<typeof judgeGame24> | null, typedRaw: string | null) => {
    const trace = buildTrace(answerMode, typed, typedRaw) // reads firstExposure before we mark it
    markPuzzleSeen(trace.numbers) // persistent seen-set → unseen-first extension + repeat flag
    sessionTracesRef.current = [...sessionTracesRef.current, trace]
    addStreamedMs(trace.puzzleDurationMs + trace.reflectionDurationMs) // mic-on time → daily cap
    savePuzzleTrace(trace)
      .then((r) => {
        // eslint-disable-next-line no-console
        console.log(`[trace] ${mode}${trace.arm ? `/${trace.arm}` : ''} #${trace.trialIndex} ${trace.puzzleId}${trace.band ? ` (${trace.band})` : ''} cond=${trace.conditionId ?? '-'} → ${r.collection}${r.fellBack ? ' (fallback)' : ''}`, r.id)
      })
      .catch((e: any) => {
        // eslint-disable-next-line no-console
        console.error('[trace] save failed', e)
        setSaveError(e?.message || 'save failed')
      })

    track('labs_puzzle_end', {
      mode,
      trial_index: puzzleIndex,
      outcome:
        trace.answerMode === 'voice-detected'
          ? 'solved_spoken'
          : trace.answerMode === 'timeout'
          ? 'timeout'
          : trace.answerMode === 'pass'
          ? 'pass'
          : trace.correct
          ? 'solved_typed'
          : 'typed_wrong',
      correct: trace.correct ? 'yes' : 'no',
      stt_engine: STT_ENGINE,
      spoke: (trace.committedTranscript || '').trim() ? 'yes' : 'no',
    })

    // Retrospective ("aha") follow-up: only on SOLVED test-mode puzzles once the regime is
    // active. The concurrent trace is already saved above, so a drop-out during the retro
    // never costs the (crown-jewel) trace.
    // Retro fires on solved puzzles: in TEST once the regime is active (trialIndex >= N),
    // and in free PLAY as an optional (skippable) extra now the core 10 are banked.
    const retroDue =
      trace.correct &&
      ((mode === 'test' && Number.isFinite(RETRO.n) && puzzleIndex >= RETRO.n) || mode === 'play')
    if (retroDue) {
      retroTranscriptRef.current = []
      setRetroSpoken('')
      setRetroInterim('')
      retroPromptedAtRef.current = performance.now()
      retroPromptedISORef.current = new Date().toISOString()
      setPhase('retro')
    } else {
      finishAdvance()
    }
  }

  // Retro answered/skipped → write the separate create-only retro doc, then advance.
  const handleRetroDone = (answer: RetroAnswer) => {
    const p = puzzle
    if (p && sessionRef.current) {
      const retro: PuzzleRetro = {
        schemaVersion: RETRO_SCHEMA_VERSION,
        sessionId: sessionRef.current.id,
        trialIndex: puzzleIndex,
        puzzleId: p.id,
        numbers: p.numbers,
        solveFelt: answer.solveFelt,
        changedTranscript: retroTranscriptRef.current.length ? retroTranscriptRef.current : null,
        changedText: answer.changedText,
        sttEngine: STT_ENGINE,
        skipped: answer.skipped,
        retroAfterN: Number.isFinite(RETRO.n) ? RETRO.n : null,
        retroCode: RETRO.code,
        promptedAt: retroPromptedISORef.current,
        answerDurationMs: Math.round(performance.now() - retroPromptedAtRef.current),
      }
      addStreamedMs(retro.answerDurationMs) // retro mic-on time → daily cap
      savePuzzleRetro(retro)
        .then((r) => {
          // eslint-disable-next-line no-console
          console.log(`[retro] #${retro.trialIndex} → ${r.collection}${r.fellBack ? ' (fallback)' : ''}`, r.id)
        })
        .catch((e: any) => {
          // eslint-disable-next-line no-console
          console.error('[retro] save failed', e)
        })
    }
    track('labs_retro', { solve_felt: answer.solveFelt || 'none', skipped: answer.skipped ? 'yes' : 'no' })
    finishAdvance()
  }

  // Opt-in extension: E more research puzzles, each retro'd (their index >= any active N).
  const keepGoingExtension = () => {
    track('labs_continue', { accepted: 'yes' })
    extendedRef.current = true
    // Extension draws from Wurgaft-200 problems this user hasn't seen (the core 10 just played
    // are already in the seen-set), so continuers accumulate more coverage of the same set.
    setSessionPuzzles((prev) => [...prev, ...buildExtensionPuzzles(EXTENSION_PUZZLES, seenPuzzleKeys())])
    startPuzzle(puzzleIndex + 1)
  }

  // End an unsolved puzzle — voluntary give-up ('pass') or the 3-minute limit ('timeout').
  // Snapshots the transcript so far (what they said while failing), records no answer, and
  // advances. Guarded against double-fire so a timeout can't race a give-up.
  const endUnsolved = (reason: 'pass' | 'timeout') => {
    if (hasCommittedRef.current) return
    hasCommittedRef.current = true
    setShowPassConfirm(false)
    const segments: ConfirmedSegment[] = transcriptRef.current
      .filter((t) => t.isFinal)
      .map((t) => ({ text: t.text.trim(), ts: t.ts }))
      .filter((s) => s.text)
    committedTranscriptRef.current = formatWithPauses(segments)
    const now = performance.now()
    committedAtRef.current = now
    reflectionEndRef.current = now
    pushEvent({ ts: Math.round(now - tsBase), type: reason === 'timeout' ? 'timeout' : 'answer_passed' })
    pushEvent({ ts: Math.round(now - tsBase), type: 'mic_off' })
    setAutoMatch(null)
    advance(reason, null, null)
  }
  endUnsolvedRef.current = endUnsolved

  const acceptDetected = () => advance('voice-detected', null, null)
  const rejectDetected = () => {
    autoRejectedRef.current = true
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_detection_rejected' })
    setPhase('entry')
  }
  const handlePass = () => {
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_passed' })
    advance('pass', null, null)
  }
  const submitTyped = (expr: string) => {
    if (!puzzle) return
    const verdict = judgeGame24(expr, puzzle.numbers)
    lastTypedRef.current = expr
    setTypedResult(verdict)
    pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'answer_typed', value: expr })
    setPhase('verdict')
  }

  // Label for the result-screen Continue button, by mode.
  const continueLabel =
    mode === 'test' ? (isLastInSession ? 'See your results' : 'Next puzzle') : mode === 'practice' ? 'Continue' : 'Next puzzle'

  // ---- render ----
  const renderScreen = () => {
    if (STT_ENGINE === 'webspeech' && gateReason !== 'ok') return <BrowserGate reason={gateReason} />

    switch (phase) {
      case 'consent':
        if (hasConsented()) return null // auto-starting via the mount effect
        return <ConsentScreen onConsent={handleStart} />

      case 'mic-error':
        return (
          <StatusScreen
            emoji="🚫"
            title="We couldn’t access your mic"
            body="The 24-game needs microphone access. Check your browser’s mic permission for this site, then try again."
            action={{ label: 'Try again', onClick: requestMic }}
          />
        )

      case 'daily-limit':
        return (
          <StatusScreen
            emoji="✋"
            title="That’s your play for today"
            body="You’ve reached today’s voice-play limit — it resets tomorrow. Thanks for helping our research!"
          />
        )

      case 'mode-choice':
        return (
          <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8 text-center">
            <Brand />
            <h2 className="mt-8 text-xl font-semibold text-gray-800">Welcome back</h2>
            <p className="mt-2 max-w-sm text-gray-600">You’ve already completed the 10-game test. Jump into free play, or take the test again.</p>
            <button type="button" onClick={() => beginPlay(undefined)} className="mt-6 w-full max-w-xs rounded-lg bg-[#820458] py-3 text-white font-semibold">
              Free play
            </button>
            <button type="button" onClick={beginTest} className="mt-3 text-sm text-gray-500 underline">
              Take the 10-game test again
            </button>
            <div className="w-full max-w-xs">
              <OtherGamesLinks current="twentyfour" />
            </div>
          </div>
        )

      case 'practice-intro':
        return (
          <StatusScreen
            emoji="👋"
            title="Quick warm-up"
            body="Before the main test, two quick practice puzzles to get used to thinking out loud — up to 3 minutes each. Solve each one to continue."
            action={{ label: 'Start warm-up', onClick: beginPractice }}
          />
        )

      case 'test-intro':
        return (
          <StatusScreen
            emoji="🎯"
            title="The 10-game test"
            body={
              <>
                Now the real thing: 10 puzzles across the full difficulty range.{' '}
                <strong className="font-semibold text-gray-800">
                  You have up to 3 minutes to attempt each puzzle.
                </strong>{' '}
                Think out loud as you solve — finishing all 10 unlocks unlimited free play.
              </>
            }
            action={{ label: 'Start the test', onClick: beginTest }}
          />
        )

      case 'play-intro':
        return (
          <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8 text-center">
            <Brand />
            <h2 className="mt-8 text-xl font-semibold text-gray-800">Free play</h2>
            <p className="mt-2 max-w-sm text-gray-600">Pick a difficulty and keep going as long as you like.</p>
            <div className="mt-6 grid w-full max-w-xs grid-cols-2 gap-3">
              {(['easy', 'medium', 'hard'] as Difficulty[]).map((d) => (
                <button key={d} type="button" onClick={() => beginPlay(d)} className="rounded-lg bg-gray-100 py-3 font-semibold capitalize text-gray-700 hover:bg-gray-200">
                  {d}
                </button>
              ))}
              <button type="button" onClick={() => beginPlay(undefined)} className="rounded-lg bg-[#820458] py-3 font-semibold text-white">
                Any
              </button>
            </div>
          </div>
        )

      case 'playing': {
        if (!puzzle) return null
        const showNudge = performance.now() - lastActivityRef.current > NUDGE_SILENCE_MS
        return (
          <div key={puzzleIndex} className="labs-puzzle-fade">
            <PuzzleView
              numbers={puzzle.numbers}
              energy={energy.energy}
              speaking={energy.speaking}
              dimmed={energy.dimmed}
              confirmedSegments={confirmedSegments}
              interimText={interimText}
              progressLabel={progressLabel}
              showNudge={showNudge}
              onCommit={handleCommit}
              onPass={() => {
                pushEvent({ ts: Math.round(performance.now() - tsBase), type: 'pass_prompt' })
                setShowPassConfirm(true)
              }}
            />
            {showPassConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
                <div className="w-full max-w-xs rounded-xl bg-white p-5 text-center shadow-xl">
                  <div className="text-2xl">⏳</div>
                  <h3 className="mt-2 font-semibold text-gray-800">Give up on this one?</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You’ve got up to 3 minutes — talking it through a little longer often cracks it. Keep going?
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowPassConfirm(false)}
                    className="mt-4 w-full rounded-lg bg-[#398874] py-3 font-semibold text-white"
                  >
                    Keep going
                  </button>
                  <button
                    type="button"
                    onClick={() => endUnsolved('pass')}
                    className="mt-2 w-full rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    Give up
                  </button>
                  {mode === 'play' && (
                    <button
                      type="button"
                      onClick={() => { window.location.href = labsHomeUrl() }}
                      className="mt-3 block w-full text-xs text-gray-400 underline"
                    >
                      Back to other games
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      }

      case 'reflection':
        if (!puzzle) return null
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <ReflectionNotice seconds={REFLECTION_SECONDS} onDone={endReflection} />
          </PuzzleStage>
        )

      case 'detected':
        if (!puzzle) return null
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <div className="text-center">
              <div className="text-3xl">🎉</div>
              <div className="mt-1 font-semibold text-gray-800">Sounds like you got it!</div>
              {autoMatch?.matchedSolution && (
                <div className="mt-2 inline-block rounded-lg bg-[#398874]/10 px-4 py-2 font-semibold text-[#398874]">
                  {prettyExpr(autoMatch.matchedSolution)} = 24
                </div>
              )}
              <button type="button" onClick={acceptDetected} className="mt-4 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold">
                {continueLabel}
              </button>
              <button type="button" onClick={rejectDetected} className="mt-3 text-sm text-gray-500 underline">
                That wasn’t my answer — let me type it
              </button>
              {mode === 'play' && (
                <>
                  <OtherGamesLinks current="twentyfour" />
                  <button type="button" onClick={() => setPhase('play-intro')} className="mt-3 block w-full text-xs text-gray-400 underline">
                    Done playing
                  </button>
                </>
              )}
            </div>
          </PuzzleStage>
        )

      case 'entry':
        if (!puzzle) return null
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <AnswerKeypad numbers={puzzle.numbers} onSubmit={submitTyped} onPass={handlePass} />
          </PuzzleStage>
        )

      case 'verdict': {
        if (!puzzle) return null
        const note = verdictNote(typedResult, puzzle.numbers)
        const exprLine = lastTypedRef.current
          ? `${prettyExpr(lastTypedRef.current)}${typedResult && typedResult.value !== null ? ` = ${typedResult.value}` : ''}`
          : null
        const wrong = typedResult ? !typedResult.correct : false
        return (
          <PuzzleStage numbers={puzzle.numbers} confirmedSegments={confirmedSegments} interimText={interimText} progressLabel={progressLabel}>
            <div className="text-center">
              <div className="text-3xl">{typedResult?.correct ? '✅' : '🤔'}</div>
              <div className="mt-1 font-semibold text-gray-800">{typedResult?.correct ? 'Correct — that’s 24!' : 'Not quite'}</div>
              {note && <p className="mt-1 text-sm text-gray-500">{note}</p>}
              {exprLine && (
                <div className={`mt-3 inline-block rounded-lg px-4 py-2 font-semibold ${wrong ? 'bg-gray-100 text-gray-700' : 'bg-[#398874]/10 text-[#398874]'}`}>
                  {exprLine}
                </div>
              )}
              {wrong && solutionExample && (
                <div className="mt-3 rounded-lg bg-gray-50 px-4 py-3">
                  <div className="text-xs text-gray-400">One way to make 24</div>
                  <div className="mt-1 font-semibold text-gray-800">{prettyExpr(solutionExample)} = 24</div>
                </div>
              )}
              <button type="button" onClick={() => advance('typed', typedResult, lastTypedRef.current)} className="mt-5 w-full rounded-lg bg-[#820458] py-3 text-white font-semibold">
                {continueLabel}
              </button>
              {mode === 'play' && (
                <>
                  <OtherGamesLinks current="twentyfour" />
                  <button type="button" onClick={() => setPhase('play-intro')} className="mt-3 block w-full text-xs text-gray-400 underline">
                    Done playing
                  </button>
                </>
              )}
            </div>
          </PuzzleStage>
        )
      }

      case 'retro':
        if (!puzzle) return null
        return (
          <RetroScreen
            key={puzzleIndex}
            numbers={puzzle.numbers}
            committedTranscript={committedTranscriptRef.current}
            spokenAnswer={retroSpoken}
            interimAnswer={retroInterim}
            commitSignal={retroCommitSignal}
            onDone={handleRetroDone}
          />
        )

      case 'continue-offer':
        return (
          <div className="flex flex-col items-center justify-center px-6 pt-16 pb-8 text-center">
            <Brand />
            <div className="mt-8 text-3xl">🙌</div>
            <h2 className="mt-3 text-xl font-semibold text-gray-800">That’s the test done — thank you!</h2>
            <p className="mt-2 max-w-sm text-gray-600">
              Fancy helping the research with a few more puzzles? Each has a quick reflection afterwards.
            </p>
            <button type="button" onClick={keepGoingExtension} className="mt-6 w-full max-w-xs rounded-lg bg-[#820458] py-3 font-semibold text-white">
              Keep going
            </button>
            <button
              type="button"
              onClick={() => {
                track('labs_continue', { accepted: 'no' })
                setPhase('summary')
              }}
              className="mt-3 text-sm text-gray-500 underline"
            >
              See my results
            </button>
          </div>
        )

      case 'summary': {
        const surveyPending = !!sessionRef.current && !surveyCompletedAt(labsFeedbackSurvey.id)
        return (
          <SummaryScreen
            plays={sessionTracesRef.current}
            saveError={saveError}
            action={{ label: '🔓 Unlock unlimited free play', onClick: () => setPhase('play-intro') }}
            feedback={
              surveyPending
                ? {
                    onGive: () => {
                      track('labs_survey_view')
                      setPhase('survey')
                    },
                    onSkip: () => {
                      markSurveyDone(labsFeedbackSurvey.id)
                      afterFeedback()
                    },
                  }
                : undefined
            }
          />
        )
      }

      case 'survey':
        if (!sessionRef.current) return null
        return (
          <SurveyScreen
            schema={labsFeedbackSurvey}
            sessionId={sessionRef.current.id}
            onDone={() => afterFeedback()}
          />
        )

      case 'panel':
        if (!sessionRef.current) return null
        return (
          <PanelSignup
            sessionId={sessionRef.current.id}
            onDone={() => {
              panelDoneRef.current = true
              setPhase('play-intro')
            }}
          />
        )

      default:
        return null
    }
  }

  return <AdLayout debugInfo={{ audioSeconds: streaming.audioSeconds }}>{renderScreen()}</AdLayout>
}

export default Game24
